// Meta Pixel (Facebook Pixel) integration
import { store } from '../app/store.js';
import { isProduction, isDevelopment } from '../utils/environment';

let pixelInitialized = false;

// Initialize Meta Pixel
export async function initMetaPixel() {
  if (pixelInitialized) return;

  try {
    // Load Meta Pixel script
    await loadMetaPixelScript();

    // Get pixel ID based on environment
    const pixelId = getPixelId();

    if (window.fbq && pixelId) {
      // Initialize pixel
      window.fbq('init', pixelId);

      // Track page view
      window.fbq('track', 'PageView');

      pixelInitialized = true;
      console.log('✅ Meta Pixel initialized');

      // Set up event listeners
      setupEventListeners();
    }
  } catch (error) {
    console.error('Failed to initialize Meta Pixel:', error);
  }
}

// Get Pixel ID based on environment
function getPixelId() {
  // Replace with your actual pixel IDs
  return isProduction()
    ? 'YOUR_PRODUCTION_PIXEL_ID'  // Production pixel
    : 'YOUR_SANDBOX_PIXEL_ID';     // Sandbox/dev pixel
}

// Load Meta Pixel script
function loadMetaPixelScript() {
  return new Promise((resolve) => {
    // Check if already loaded
    if (window.fbq) {
      resolve();
      return;
    }

    // Facebook Pixel Code
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');

    // Resolve after a short delay to ensure script is loaded
    setTimeout(resolve, 100);
  });
}

// Set up event listeners for tracking
function setupEventListeners() {
  // Track screen views
  document.addEventListener('analytics:screen', (event) => {
    const screenName = event.detail?.screen_name;
    if (screenName) {
      trackEvent('ViewContent', {
        content_name: screenName,
        content_category: 'screen'
      });
    }
  });

  // Track when user reaches certain milestones
  document.addEventListener('screen:loaded', (event) => {
    const screen = event.detail?.screen;

    // Track key milestones
    switch (screen?.id) {
      case 'email':
        trackEvent('Lead', {
          content_name: 'Email Capture Screen'
        });
        break;

      case 'results':
        trackEvent('CompleteRegistration', {
          status: 'analysis_complete',
          value: store.get('email')
        });
        break;

      case 'paywall':
        trackEvent('InitiateCheckout', {
          content_name: 'Paywall Screen',
          content_ids: ['annual_plan'],
          value: 39.99,
          currency: 'USD'
        });
        break;

      case 'success':
        const email = store.get('email');
        trackEvent('Purchase', {
          value: 39.99,
          currency: 'USD',
          content_name: 'Annual Plan',
          content_ids: ['com.shamruk.velvet.y40'],
          content_type: 'product',
          num_items: 1
        });
        break;
    }
  });

  // Track checkout events
  document.addEventListener('checkout:open', () => {
    trackEvent('AddToCart', {
      content_name: 'Annual Plan',
      content_ids: ['com.shamruk.velvet.y40'],
      content_type: 'product',
      value: 39.99,
      currency: 'USD'
    });
  });

  // Track form submissions
  document.addEventListener('form:submit', (event) => {
    const formName = event.detail?.form_name;
    trackEvent('SubmitApplication', {
      fb_content_name: formName
    });
  });
}

// Track event to Meta Pixel
export function trackEvent(eventName, parameters = {}) {
  if (!window.fbq) {
    console.warn('Meta Pixel not initialized');
    return;
  }

  // Add common parameters
  const enrichedParams = {
    ...parameters,
    source: 'web2app',
    version: 'v5',
    user_id: localStorage.getItem('lunai_user_id')
  };

  // Standard events use 'track', custom events use 'trackCustom'
  const standardEvents = [
    'PageView', 'ViewContent', 'Search', 'AddToCart', 'AddToWishlist',
    'InitiateCheckout', 'AddPaymentInfo', 'Purchase', 'Lead',
    'CompleteRegistration', 'Contact', 'CustomizeProduct', 'Donate',
    'FindLocation', 'Schedule', 'StartTrial', 'SubmitApplication', 'Subscribe'
  ];

  if (standardEvents.includes(eventName)) {
    window.fbq('track', eventName, enrichedParams);
  } else {
    window.fbq('trackCustom', eventName, enrichedParams);
  }

  // Log in development
  if (isDevelopment()) {
    console.log('📊 Meta Pixel Event:', eventName, enrichedParams);
  }
}

// Track custom conversions
export function trackCustomConversion(conversionName, value = null) {
  trackEvent(conversionName, {
    value: value,
    currency: 'USD'
  });
}

// Update user data for better targeting
export function updatePixelUserData(userData) {
  if (!window.fbq) return;

  // Meta Pixel advanced matching
  const advancedMatching = {};

  if (userData.email) {
    advancedMatching.em = userData.email.toLowerCase();
  }

  if (userData.firstName) {
    advancedMatching.fn = userData.firstName.toLowerCase();
  }

  if (userData.lastName) {
    advancedMatching.ln = userData.lastName.toLowerCase();
  }

  if (userData.phone) {
    advancedMatching.ph = userData.phone.replace(/\D/g, '');
  }

  if (userData.dateOfBirth) {
    advancedMatching.db = userData.dateOfBirth.replace(/-/g, '');
  }

  if (userData.gender) {
    advancedMatching.ge = userData.gender.toLowerCase()[0]; // 'm' or 'f'
  }

  if (userData.city) {
    advancedMatching.ct = userData.city.toLowerCase();
  }

  if (userData.state) {
    advancedMatching.st = userData.state.toLowerCase();
  }

  if (userData.zip) {
    advancedMatching.zp = userData.zip;
  }

  if (userData.country) {
    advancedMatching.country = userData.country.toLowerCase();
  }

  // Update pixel with advanced matching data
  if (Object.keys(advancedMatching).length > 0) {
    window.fbq('init', getPixelId(), advancedMatching);
  }
}

// Export for global access
window.metaPixel = {
  track: trackEvent,
  trackCustomConversion,
  updateUserData: updatePixelUserData
};